import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CustomsErrorsCriteria, SortDirection } from '@e-origin/shared';

import { showErrorToast } from '@hooks/showErrorToast';
import { apiClient } from '@utils';

export const defaultCustomsErrorsCriteria: CustomsErrorsCriteria = {
  filters: { searchQuery: '' },
  pagination: { page: 1, size: 10 },
  sorting: { field: 'errorMessage', direction: SortDirection.DESC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): CustomsErrorsCriteria => {
  const filters: CustomsErrorsCriteria['filters'] = {};
  filters.searchQuery = searchParams.get('searchQuery') || '';

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'errorMessage';
  const sortDir = (searchParams.get('sortDir') || SortDirection.DESC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: CustomsErrorsCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialCustomsErrorsCriteria = (searchParams: URLSearchParams): CustomsErrorsCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultCustomsErrorsCriteria;
};

export const useCustomsErrorsCriteriaUrlSync = (criteria?: CustomsErrorsCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

export const useAdminCustomsErrors = (criteria: CustomsErrorsCriteria = defaultCustomsErrorsCriteria) => {
  return useQuery({
    queryKey: ['admin-customs-errors-list', criteria],
    queryFn: async () => {
      try {
        const result = await apiClient.customsError.retrieveCustomsErrors.mutation({ body: { criteria } });
        if (result.status === 201) {
          return result.body || { data: { customsErrors: [], totalItems: 0 } };
        } else {
          throw new Error(`Unexpected response status: ${result.status}`);
        }
      } catch (error) {
        showErrorToast(error, 'Failed to fetch customs errors list');
        return { data: { customsErrors: [], totalItems: 0 } };
      }
    },
  });
};
